import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { signedMemoryImageUrl, TRANSFORMS } from "@/lib/imageUrl";
import { useMemoryArtefacts } from "@/hooks/queries/useMemoryArtefacts";

const signedUrlCache = new Map<string, string>();
const fullResUrlCache = new Map<string, string>();

interface MemoryPhotoGridProps {
  memoryId: string;
  /** Used as the primary tile placeholder while artefacts load on first-ever
   *  view (TanStack Query persists between sessions, so this only flickers
   *  on a cold device). Pass `memories.image_url`. */
  fallbackImageUrl?: string | null;
  size?: "sm" | "md";
  onPhotoClick?: (url: string) => void;
  onOverflowClick?: () => void;
}

const SIZE_CLASS: Record<NonNullable<MemoryPhotoGridProps["size"]>, string> = {
  sm: "aspect-[3/2] max-h-36",
  md: "aspect-[3/2] max-h-64",
};

async function signPath(path: string): Promise<string | null> {
  const cached = signedUrlCache.get(path);
  if (cached) return cached;
  const url = await signedMemoryImageUrl(path, TRANSFORMS.gridThumb);
  if (url) signedUrlCache.set(path, url);
  return url;
}

async function signPathFullRes(path: string): Promise<string | null> {
  const cached = fullResUrlCache.get(path);
  if (cached) return cached;
  const url = await signedMemoryImageUrl(path);
  if (url) fullResUrlCache.set(path, url);
  return url;
}

export default function MemoryPhotoGrid({
  memoryId,
  fallbackImageUrl,
  size = "md",
  onPhotoClick,
  onOverflowClick,
}: MemoryPhotoGridProps) {
  const { t } = useTranslation();
  const { artefacts } = useMemoryArtefacts(memoryId);

  // Decide which paths to render. Trigger guarantees ≥1 artefact when
  // image_url is set, but on cold load artefacts arrive after the card
  // mounts — fall back to image_url so the primary tile shows immediately.
  const paths: string[] =
    artefacts.length > 0
      ? artefacts.slice(0, 2).map((a) => a.storage_path)
      : fallbackImageUrl
        ? [fallbackImageUrl]
        : [];
  const total = artefacts.length > 0 ? artefacts.length : paths.length;

  const [signedUrls, setSignedUrls] = useState<(string | null)[]>(() =>
    paths.map((p) => signedUrlCache.get(p) ?? null),
  );
  const [fullResUrls, setFullResUrls] = useState<(string | null)[]>(() =>
    paths.map((p) => fullResUrlCache.get(p) ?? null),
  );

  useEffect(() => {
    if (paths.length === 0) {
      setSignedUrls([]);
      setFullResUrls([]);
      return;
    }
    let cancelled = false;
    Promise.all([
      Promise.all(paths.map(signPath)),
      Promise.all(paths.map(signPathFullRes)),
    ]).then(([thumbUrls, hiResUrls]) => {
      if (!cancelled) {
        setSignedUrls(thumbUrls);
        setFullResUrls(hiResUrls);
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paths.join("|")]);

  if (paths.length === 0) return null;

  const overflowCount = total > 2 ? total - 2 : 0;
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  // ── Single photo: full-width tile (preserves today's look) ────────────────
  if (total <= 1) {
    const url = signedUrls[0];
    return (
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          if (url) onPhotoClick?.(fullResUrls[0] ?? url);
        }}
        className={`relative w-full ${SIZE_CLASS[size]} rounded-xl overflow-hidden bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary`}
        aria-label={t("memory.detail.viewFullscreen")}
      >
        {url && (
          <img
            src={url}
            alt={t("alt.memoryImage")}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </button>
    );
  }

  // ── 2 photos: 1 large + 1 small (small spans both right rows) ─────────────
  // ── 3+ photos: 1 large + S1 + grey "+N" tile ──────────────────────────────
  return (
    <div
      className={`grid grid-cols-3 grid-rows-2 gap-1 w-full ${SIZE_CLASS[size]} rounded-xl overflow-hidden`}
    >
      {/* Large tile (left, 2×2) */}
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          if (signedUrls[0]) onPhotoClick?.(fullResUrls[0] ?? signedUrls[0]);
        }}
        className="col-span-2 row-span-2 relative bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        aria-label={t("memory.detail.viewFullscreen")}
      >
        {signedUrls[0] && (
          <img
            src={signedUrls[0]}
            alt={t("alt.memoryImage")}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </button>

      {/* Top-right small tile (always a real photo) */}
      <button
        type="button"
        onClick={(e) => {
          stop(e);
          if (signedUrls[1]) onPhotoClick?.(fullResUrls[1] ?? signedUrls[1]);
        }}
        className={`relative bg-muted/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${overflowCount === 0 ? "row-span-2" : ""}`}
        aria-label={t("memory.detail.viewFullscreen")}
      >
        {signedUrls[1] && (
          <img
            src={signedUrls[1]}
            alt={t("alt.memoryImage")}
            className="absolute inset-0 h-full w-full object-cover"
          />
        )}
      </button>

      {/* Bottom-right grey "+N" tile (only when 3+ photos) */}
      {overflowCount > 0 && (
        <button
          type="button"
          onClick={(e) => {
            stop(e);
            onOverflowClick?.();
          }}
          className="relative flex items-center justify-center bg-muted text-foreground/70 font-semibold text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          aria-label={`+${overflowCount}`}
        >
          +{overflowCount}
        </button>
      )}
    </div>
  );
}
